import { type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type DefaultValues,
  type FieldValues,
  type Path,
  type UseFormReturn
} from 'react-hook-form';
import { type ZodType } from 'zod';
import {
  Dialog,
  DialogShell,
  DialogShellHeader,
  DialogShellBody,
  DialogShellFooter,
  UnsavedChangesConfirmDialog
} from '@bliks-io/ui';
import type { EntitySuggestionKind } from '@bliks-io/contracts';
import { DuplicateCandidatesPanel } from './shared/DuplicateCandidatesPanel';
import type { SuggestionPickEvent } from './shared/suggestionPickEvent';
import {
  useEntitySuggestionDialog,
  type SuggestionKindArgs,
  type UseEntitySuggestionDialogArgs
} from './hooks/useEntitySuggestionDialog';

export interface EntitySuggestionDialogRenderArgs<TForm extends FieldValues> {
  form: UseFormReturn<TForm>;
  /**
   * Pre-rendered `<DuplicateCandidatesPanel>` for the configured kind. Place
   * it inside `renderFields` near the primary field set so duplicates surface
   * before the user fills in the rest of the form.
   */
  candidatesPanel: ReactNode;
}

interface EntitySuggestionDialogBaseProps<TForm extends FieldValues> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Source-link refinement: exactly one of these must be set so the backend
   * can attach the suggestion to its originating recording or process.
   */
  sourceRecordingId?: string;
  /** Process behind a node-anchored source — cache-seed hint, not wire data. */
  seedProcessId?: string;
  /**
   * Draft-editor origin: `sourceRecordingId` is a draft node's `stableStepId`,
   * so create and link post to the draft routes under this process.
   */
  draftProcessId?: string;
  /** Called when the user picks an existing entity from the duplicate panel. */
  onUseExistingEntity?: (entityId: string) => void;
  /**
   * Fired right after a suggestion is persisted (`kind: 'created'`) or adopted
   * from the duplicate panel (`kind: 'adopted'`), so the slot that opened the
   * dialog can append `{ type: 'suggestion', id }` to its array. Required if
   * the caller wants the suggestion to appear in the slot — the contract makes
   * the caller responsible for that write. Flows that withdraw on cancel track
   * `'created'` events only: an adopted suggestion predates this dialog and is
   * never theirs to withdraw.
   */
  onSuggestionCreated?: (event: SuggestionPickEvent) => void;

  /** Icon component shown in the dialog header. */
  icon: ComponentType<{ className?: string }>;
  titleKey: string;
  descriptionKey: string;
  submitLabelKey: string;

  schema: ZodType<TForm>;
  defaultValues: DefaultValues<TForm>;
  primaryFieldName: Path<TForm>;
  secondaryFieldName?: Path<TForm>;
  buildCandidatesPayload: UseEntitySuggestionDialogArgs<TForm>['buildCandidatesPayload'];

  /** Panel kind — typically matches `kind` but isolated so future kinds can map differently. */
  duplicateCandidatesPanelKind: EntitySuggestionKind;

  /**
   * Render the entity-specific form-field block inside the dialog body. The
   * generic provides the pre-rendered `candidatesPanel` so the caller can
   * place it inside the primary section.
   */
  renderFields: (args: EntitySuggestionDialogRenderArgs<TForm>) => ReactNode;
}

/** `kind` + `toApiPayload` arrive as the correlated union (drives candidate
 *  lookup + the typed create request). */
export type EntitySuggestionDialogProps<TForm extends FieldValues> =
  EntitySuggestionDialogBaseProps<TForm> & SuggestionKindArgs<TForm>;

/**
 * Generic entity-suggestion creation dialog. Owns the chrome
 * (Dialog/DialogShell/Header/Body/Footer + unsaved-changes prompt) and wires
 * `useEntitySuggestionDialog`. Per-kind concerns (icon, copy, schema, field
 * block) are injected via props.
 */
export function EntitySuggestionDialog<TForm extends FieldValues>(
  props: EntitySuggestionDialogProps<TForm>
) {
  const {
    open,
    icon: Icon,
    titleKey,
    descriptionKey,
    submitLabelKey,
    duplicateCandidatesPanelKind,
    renderFields
  } = props;
  const { t } = useTranslation();

  const {
    form,
    candidates,
    isCheckingDuplicates,
    isSubmitting,
    busy,
    adoptingId,
    handleFormSubmit,
    handleUseExisting,
    showCloseConfirmation,
    handleOpenChange,
    handleDiscardChanges,
    handleStay
    // `props` stays whole here: destructuring kind/toApiPayload separately
    // would drop the correlation the hook needs for the cast-free create
    // request (extra props are structurally harmless).
  } = useEntitySuggestionDialog<TForm>(props);

  const candidatesPanel = (
    <DuplicateCandidatesPanel
      candidates={candidates}
      kind={duplicateCandidatesPanelKind}
      onUseExisting={handleUseExisting}
      isChecking={isCheckingDuplicates}
      adoptingId={adoptingId}
      disabled={busy}
    />
  );

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogShell size="lg">
          <form
            noValidate
            onSubmit={form.handleSubmit(handleFormSubmit)}
            className="flex flex-1 min-h-0 flex-col"
          >
            <DialogShellHeader
              icon={<Icon className="h-5 w-5" />}
              iconTone="green"
              title={t(titleKey)}
              subtitle={t(descriptionKey)}
              onClose={() => handleOpenChange(false)}
            />
            <DialogShellBody>{renderFields({ form, candidatesPanel })}</DialogShellBody>
            <DialogShellFooter
              secondary={{
                label: t('common.cancel'),
                onClick: () => handleOpenChange(false),
                disabled: busy
              }}
              primary={{
                label: t(submitLabelKey),
                type: 'submit',
                loading: isSubmitting,
                disabled: busy
              }}
            />
          </form>
        </DialogShell>
      </Dialog>

      <UnsavedChangesConfirmDialog
        open={showCloseConfirmation}
        onStay={handleStay}
        onDiscard={handleDiscardChanges}
      />
    </>
  );
}
